import { useMutation } from '@tanstack/react-query';
import { rpcClient } from '@/api/rpc/client';
import type { SuggestNameInput } from '@erp/rpc';

/**
 * AI naming for anything. Hand it whatever object describes the thing being
 * named and get a short human-facing name back from a fast model.
 *
 * ```tsx
 * const { suggestName, isPending } = useSuggestName();
 *
 * const name = await suggestName(filters, { kind: 'saved view', maxLength: 40 });
 * const better = await suggestName(filters, {
 *   kind: 'saved view',
 *   rename: { currentName: view.name, reason: 'it no longer matches the filters' },
 * });
 * ```
 *
 * Every option is passed per call rather than captured at hook level, so
 * `suggestName` closes over nothing but React Query's stable `mutateAsync` —
 * the compiler keeps its identity stable across renders, which matters because
 * each call costs a model request. Callers that reuse the same options can
 * hoist them into a module constant and spread them.
 *
 * The first argument is always exactly ONE subject — an array is passed
 * through as a single JSON value, not expanded. To describe the thing with
 * several separate objects, pass `subjects` in the options instead.
 *
 * No cache interaction — the name feeds a form field, never a query cache,
 * so there's nothing to invalidate.
 */

type NamingOptions = Partial<SuggestNameInput>;

export function useSuggestName() {
  // react-doctor-disable-next-line react-doctor/query-mutation-missing-invalidation
  const mutation = useMutation({
    mutationFn: rpcClient.core.aiNaming.suggestName,
  });

  const { mutateAsync } = mutation;

  const suggestName = async (subject: unknown, options: NamingOptions = {}) => {
    const { name } = await mutateAsync({ subjects: [subject], ...options });
    return name;
  };

  return {
    suggestName,
    /** The most recent result, if any. */
    name: mutation.data?.name,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
